import type { DeepPartial } from './keys';
import type { DomainCopy } from './en';

/**
 * **THE DOMAIN PACKAGE'S WORDS IN SPANISH** (3.0.11). Drafted from `docs/handoff/spanish/glossary.csv`, which Evan's
 * reviewer approved on 28 Sep: neutral Latin American Spanish, the tú form; product names stay English (Kiddie Kredit,
 * Kredit Score, Bamboo Bucks, BB, Family Plus, Village Square, Quest Jr., Oops); dates day-month; every value slotted
 * in where the English slots it. A line whose exact English was already reviewed — the templates, in
 * `notifications.csv` — takes that wording and is marked `reviewed` in `esSources`; every other line is `draft` until
 * the reviewer returns it. The content-admin words follow the reviewed portal tab (escena, opción, casilla, biblioteca
 * de íconos, estante, socio, borrador).
 *
 * Spanish plurals are not "+s" (misión → misiones), so every counted line spells both forms here.
 */
const pl = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

export const es: DeepPartial<DomainCopy> = {
  templates: {
    greeting: (firstName) => `Hola, ${firstName}`,
    toApprove: (n) => `${n} por aprobar`,
    toApproveTitle: 'Por aprobar',
    ageBand: { prereader: 'prelector', reader: 'lector' },
    photo: 'foto',
    tapToSeePhoto: 'toca para ver la foto',
    approve: 'Aprobar',
    possessive: (name) => `de ${name}`,
    photoFrom: (owner) => `Foto del dispositivo ${owner}`,
    readAloudTo: (name) => `Léele en voz alta a ${name}:`,
    quotedNote: (note) => `“${note}”`,
    redeemTitle: (title) => `Canjear: ${title}`,
    rewardRequest: 'Solicitud de recompensa',
    balance: (balance) => `saldo ${balance}`,
    scoreOk: (needsScore) => `puntaje OK (necesita ${needsScore})`,
    saveChild: (name) => `Guardar a ${name}`,
    howDoesUse: (name) => `¿Cómo usa ${name} la app?`,
    pickChoresFor: (name) => `Elige algunas tareas para ${name}`,
    addChores: (n) => `Agregar ${pl(n, 'tarea', 'tareas')}`,
    noChoresYet: (name) => `${name} aún no tiene tareas`,
    noChoresYetBody: (owner) =>
      `Agrega algunas y el puntaje ${owner} empezará a crecer la primera vez que termine una.`,
    wantsTitle: (firstName, rewardTitle) => `${firstName} quiere ${rewardTitle}`,
    chargedOnRedeem: 'se cobra al canjear',
    ofBalance: (balance) => `de ${balance}`,
    wantsBody: (price, balance) =>
      `${pl(price, 'Bamboo Buck', 'Bamboo Bucks')} · ${balance} ahorrados`,
    cancelledTitle: (firstName, rewardTitle) => `${firstName} canceló ${rewardTitle}`,
    cancelledBody: (bb) => `${bb} de vuelta en la billetera`,
    paidOutBy: (kidName) => `Pagado por ${kidName}`,
    paidOut: 'Pagado',
    dollarLine: (dollars, rate, one) => `= ${dollars} · ${rate} ${one ? 'es' : 'son'} $1`,
    badgeCountPill: (n) => pl(n, 'insignia', 'insignias'),
    adventureEyebrow: (displayNumber) => `AVENTURA ${displayNumber}`,
    missionsDoneOf: (done, total) => `${done} de ${total}`,
    missionNodeLabel: (order, title) => `Misión ${order} · ${title}`,
    startMissionCta: (order) => `Empezar misión ${order}`,
    quickCheckEyebrow: (step, total) => `REPASO RÁPIDO · ${step} DE ${total}`,
    adventureCompleteEyebrow: (displayNumber) => `AVENTURA ${displayNumber} COMPLETA`,
    newBadgeLine: (earned, total) => `Insignia nueva · ${earned} de ${total}`,
    missionsRightLine: (missions, correct, total) =>
      `${pl(missions, 'misión', 'misiones')} · ${correct} de ${total} bien`,
    streakKeptLine: (day) => `Racha a salvo · día ${day}`,
    adventureUnlockedLine: (displayNumber, title) =>
      `Aventura ${displayNumber} desbloqueada · ${title}`,
    badgesEarnedPill: (n) => `${n} ganadas`,
    badgesToGoPill: (n) => `faltan ${n}`,
    badgeSharedTitle: (child) => `${child} compartió una insignia`,
    badgeSharedBody: (badgeCaption) => `${badgeCaption}. Toca para verla.`,
    rewardNew: {
      familyTitle: (title) => `Recompensa nueva: ${title}`,
      familyBody: (bbPrice) => pl(bbPrice, 'Bamboo Buck', 'Bamboo Bucks'),
      orgTitle: (groupName) => `Recompensa nueva de ${groupName}`,
      orgBody: (unlockTask) => `Termina ${unlockTask} para desbloquearla`,
    },
    screenTime: {
      doneTitle: (firstName) =>
        `${firstName} terminó las tareas de hoy. Apruébalas para desbloquear el tiempo de pantalla.`,
      askTitle: (firstName) => `${firstName} pidió desbloquear el tiempo de pantalla.`,
      backTitle: '¡Volvió el tiempo de pantalla!',
      backApproved: 'Tus tareas están aprobadas.',
      backUnlocked: (kidName) => `${kidName} lo desbloqueó por hoy.`,
      backPaused: (kidName) => `${kidName} pausó el bloqueo por una semana.`,
      approvedEarly: 'Tus tareas están aprobadas. Hoy el tiempo de pantalla no se pausará.',
      turnedOff: (kidName) => `${kidName} apagó el bloqueo del tiempo de pantalla.`,
      changed: (kidName) => `${kidName} cambió cuándo se pausa el tiempo de pantalla.`,
    },
    clubJoinedTitle: (club) =>
      `${club} ya está en Kiddie Kredit. Ahora puede invitar a tu comunidad.`,
    clubApplicationThanks: (name) => `Gracias, ${name}. Te contactaremos en unos días.`,
  },

  time: {
    weekdaysLong: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    weekdaysShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    monthsShort: [
      'ene.',
      'feb.',
      'mar.',
      'abr.',
      'may.',
      'jun.',
      'jul.',
      'ago.',
      'sep.',
      'oct.',
      'nov.',
      'dic.',
    ],
    am: 'a. m.',
    pm: 'p. m.',
    clock: (hour, minute, amPm) => `${hour}:${minute} ${amPm}`,
    shortDate: (day, month) => `${day} ${month}`,
    dateHeader: (weekday, day, month) => `${weekday}, ${day} ${month}`,
    weekRangeSameMonth: (startDay, endDay, month) => `${startDay}–${endDay} ${month}`,
    weekRange: (startDay, startMonth, endDay, endMonth) =>
      `${startDay} ${startMonth} – ${endDay} ${endMonth}`,
    dayMonthYear: (day, month, year) => `${day} ${month} ${year}`,
    monthYear: (month, year) => `${month} ${year}`,
    todayCapital: 'Hoy',
    yesterdayCapital: 'Ayer',
    today: 'hoy',
    yesterday: 'ayer',
    daysAgo: (n) => `hace ${n} días`,
    upperLocale: 'es-US',
  },

  schedule: {
    daily: 'diaria',
    everyDay: 'todos los días',
    schoolDays: 'días de escuela',
    monFri: 'lun–vie',
    weekdaysPlural: [
      'los domingos',
      'los lunes',
      'los martes',
      'los miércoles',
      'los jueves',
      'los viernes',
      'los sábados',
    ],
  },

  sizes: { S: 'Pequeña', M: 'Mediana', L: 'Grande' },

  bands: {
    Building: 'En construcción',
    Growing: 'Creciendo',
    Good: 'Bien',
    Great: 'Muy bien',
    Super: 'Súper',
  },

  oops: {
    lowerLocale: 'es-US',
    reasonLabel: (reasonText) => `Oops: ${reasonText}`,
    noticeTitle: (giver, child, reasonLabel) => `${giver} le dio a ${child} un ${reasonLabel}.`,
    scorePart: (displayPoints) => `−${displayPoints} en el puntaje`,
  },

  gate: {
    needsScore: (minScore) => `necesita un puntaje de ${minScore}`,
    noScoreYet: (minScore) =>
      `aún no hay puntaje: no se puede cumplir el requisito antes de la primera tarea aprobada (necesita ${minScore})`,
  },

  org: {
    sees: {
      firstName: 'Su nombre',
      avatar: 'Su avatar',
      ageBand: 'Su rango de edad — no su cumpleaños',
      groups: 'En qué grupos del club está',
      assignments: 'Las asignaciones del propio club para tu hijo o hija',
      tasks: 'Cuántas tareas del club terminó a tiempo',
      enrolledAt: 'El día en que te uniste',
      scoreBand: 'El NIVEL de su puntaje — "Bien", nunca el número',
    },
    neverSees: {
      lastName: 'Su apellido',
      dob: 'Su fecha de nacimiento',
      photos: 'Cualquier foto de tareas — esas se quedan en tu familia',
      scoreNumber: 'Su puntaje como número',
      bb: 'Sus Bamboo Bucks, o en qué los gastó',
      siblings: 'Sus hermanos y hermanas',
      familyChores: 'Las tareas que pones en casa',
      wishes: 'Lo que tu hijo o hija desea',
      oops: 'Cualquier Oops, o por qué se dio',
      parentContact: 'Tu correo o cualquier cosa sobre ti',
    },
    neverSeesShort: {
      identity: 'El apellido o el cumpleaños de tu hijo o hija',
      photos: 'Fotos de tareas, deseos y Oops — se quedan en tu familia',
      home: 'Las tareas que pones en casa, o sus Bamboo Bucks',
      score: 'Su Kredit Score, salvo que lo actives',
    },
    initialsTooLong: (max, fits, tag) =>
      `las iniciales van en un disco de insignia de ${max} caracteres — "${fits}" cabe, "${tag}" no. La forma corta legible va en shortName`,
  },

  pandas: {
    'panda-white': 'el panda blanco y negro con suéter',
    'panda-orange': 'el panda naranja con gorra',
    'panda-blue': 'el panda azul con lentes',
    'panda-red': 'el panda rojo con una flor',
    'panda-purple': 'el panda morado con audífonos',
    'panda-green': 'el panda verde con gorro de pompón',
    'panda-yellow': 'el panda amarillo con bufanda',
    'panda-brown': 'el panda café con moño',
  },

  village: {
    feeLine: 'Podríamos recibir una comisión si te registras.',
    partnerTag: 'Socio',
    helpfulThanks: 'Gracias por contarnos.',
    problems: {
      pickShelf: 'elige un estante',
      title: 'un título',
      titleMax: (max) => `un título de ${max} caracteres o menos`,
      lineMax: (max) => `una línea de ${max} caracteres o menos`,
      ages: 'edades de menor a mayor, de 0 a 18',
      pictureKinds: 'una imagen solo en una guía o un imprimible',
      picture: 'una imagen de la biblioteca de íconos',
      printableMinutes: 'cuánto tarda, en minutos',
      readingTime: 'un tiempo de lectura en minutos',
      guideText: 'el texto de la guía',
      shorterText: 'un texto más corto',
      whichPrintable: 'qué imprimible',
      eventStart: 'cuándo empieza el evento',
      register: 'dónde registrarse',
      buttonGoes: 'adónde lleva el botón',
      whichPartner: 'qué socio',
      partnerShelf: 'la tarjeta de un socio va en Ayuda con tu dinero u Ofertas de socios',
      https: 'un enlace que empiece con https://',
      buttonWords: 'el texto del botón',
      pictureDraft: 'una imagen publicada (este ícono aún es un borrador)',
      pictureRetired: 'una imagen que todavía se ofrezca (este ícono está retirado)',
    },
    logo: {
      type: 'un archivo PNG o SVG',
      file: 'el archivo',
      size: 'un archivo de 2 MB o menos',
      png: 'un archivo PNG',
      svg: 'un archivo SVG',
    },
  },

  help: {
    question: 'una pregunta',
    questionMax: (max) => `una pregunta de ${max} caracteres o menos`,
    answer: 'una respuesta',
    paragraphsMax: (max) => `una respuesta de ${max} párrafos o menos`,
    paragraphMax: (max) => `párrafos de ${max} caracteres o menos`,
    order: 'un orden en número entero',
  },

  contentTeam: {
    soloPublishNote: 'publicada por su propio autor, el único Publisher',
  },

  adventure: {
    needsTitle: 'una aventura necesita un título',
    learnOff: (title) => `Quitó "Lo que aprenderás" de ${title}`,
    learnNeedsWords: '"Lo que aprenderás" necesita texto; envía null para quitarlo',
    learnEdited: (title) => `Editó "Lo que aprenderás" de ${title}`,
    noLearnToApprove: 'no hay "Lo que aprenderás" que aprobar',
    learnApproved: (title) => `Aprobó "Lo que aprenderás" de ${title}`,
    learnWithdrew: (title) => `Retiró la aprobación de "Lo que aprenderás" de ${title}`,
    edited: (title) => `Editó la aventura ${title}`,
    reorder: (missions) =>
      `el nuevo orden debe nombrar cada una de las ${missions} misiones de la aventura exactamente una vez`,
    nothingToPublish: 'nada que publicar: lo publicado es la versión más reciente',
    approveLearnFirst:
      'aprueba "Lo que aprenderás" antes de publicar: la línea aprobada sigue publicada hasta entonces',
  },

  questWhere: {
    beatReader: (beat) => `Escena ${beat} · lector`,
    beatPrereader: (beat) => `Escena ${beat} · prelector`,
    beatChoice: (beat, choice) => `Escena ${beat} · opción ${choice}`,
    beatChoiceConsequence: (beat, choice) => `Escena ${beat} · consecuencia de la opción ${choice}`,
    question: (question) => `Pregunta ${question}`,
    questionPrereader: (question) => `Pregunta ${question} · prelector`,
    questionTile: (question, tile) => `Pregunta ${question} · casilla ${tile}`,
    questionTilePicture: (question, tile) => `Pregunta ${question} · imagen de la casilla ${tile}`,
    questionTiles: (question) => `Pregunta ${question} · casillas`,
    questionFeedbackRight: (question) => `Pregunta ${question} · comentario si es correcta`,
    questionFeedback: (question) => `Pregunta ${question} · comentario`,
    questionAnswer: (question) => `Pregunta ${question} · respuesta`,
    missionPicture: 'Imagen de la misión',
    badge: 'Insignia',
    mission: 'Misión',
    readerScript: 'Guion para lectores',
    prereaderScript: 'Guion para prelectores',
  },

  questDiff: {
    title: 'Título',
    levelName: 'Nombre del nivel',
    mechanic: 'Mecánica',
    pointsAt: 'Lleva a',
    badge: 'Insignia',
    picture: 'Imagen',
    none: 'ninguno',
    typeChosen: 'texto, elegido',
    fromTileId: 'del id de la casilla',
  },

  questCheck: {
    rules: {
      'spoken-missing': 'Sin forma hablada',
      'beat-count': 'Número de escenas',
      'too-long': 'Pasa el límite de líneas',
      'answer-not-tile': 'La respuesta no es una casilla',
      'tile-count': 'Número de casillas',
      'mixed-row': 'Fila mixta de imagen y texto',
      'unknown-icon': 'Ícono desconocido',
      'retired-icon': 'Ícono retirado',
      'draft-icon': 'Ícono aún sin publicar',
      'no-consequence': 'Opción sin consecuencia',
      'consequence-digits': 'Cifras en una consecuencia',
      bb: '“BB” donde lee un niño',
      'parent-word': 'Mamá o papá en un texto para niños',
      pronoun: 'Pronombre sobre un niño',
      unreviewed: 'Guion sin revisar',
      duplicate: 'Repite otra misión',
      'same-icon': 'El mismo ícono dos veces',
      'reading-level': 'Nivel de lectura',
      'long-mission': 'Más larga que las demás',
      'line-break': 'Salto de línea en el texto',
    },
    notInLibrary: (key) => `“${key}” no está en la biblioteca de íconos.`,
    notPublished: (key) =>
      `“${key}” aún no está publicado. Publícalo primero en la biblioteca de íconos.`,
    beatCount: (beats) => `Una misión de historia tiene 2 o 3 escenas. Esta tiene ${beats}.`,
    nothingToHear: 'Nada que un prelector pueda escuchar.',
    noConsequence: 'Esta opción no tiene consecuencia.',
    tileCount: (tiles) => `Una pregunta tiene de 2 a 4 casillas. Esta tiene ${tiles}.`,
    answerNotTile: 'La respuesta no es una de las casillas.',
    retired: (key) =>
      `“${key}” está retirado. Se sigue mostrando donde ya se usa, pero elige otro para el contenido nuevo.`,
    retiredName: (art, key) =>
      `“${art}” es un nombre retirado. Se sigue mostrando, como “${key}”, en el contenido que ya lo usa.`,
    useKey: (key) => `Usar ${key}`,
    mixedRow: (pictures, tiles, missing, count) =>
      `${pictures} de ${tiles} casillas tienen imagen; ${missing} ${count === 1 ? 'se muestra' : 'se muestran'} como texto, así que parece que a la fila le faltan íconos.`,
    drawAllAsType: 'Mostrar todas las casillas como texto',
    sameIcon: (key) => `Dos casillas muestran “${key}”.`,
    bb: 'Escribe “Bamboo Bucks”, o no pongas la unidad.',
    parentWord: (word) => `“${word}”. La regla es neutral: “un adulto”.`,
    pronoun: (word) => `“${word}” habla de un niño que no es Kai ni Pip.`,
    digits: 'Escribe los números con letras. Las cantidades de dinero pueden quedar en cifras.',
    digitsFix: 'Escribir los números con letras',
    lineBreak: 'La app pone los saltos de línea, así que este se quita.',
    lineBreakFix: 'Quitar el salto de línea',
    readingLevel: (words, spoken, limit) =>
      `Una oración de ${words} palabras. ${spoken ? 'Un prelector' : 'Un lector de 9 a 12 años'} entiende hasta ${limit}.`,
    tooLong: (lines, holds) => `${lines} líneas a 360pt. Caben ${holds}.`,
    needsReview: 'Necesita una revisión.',
    duplicate: (label, where, text) => `Parecido a ${label}, ${where}: “${text}”`,
    longMission: (words, median) =>
      `${words} palabras, frente a ${median} de una misión típica de esta aventura.`,
  },
};

type Status = { status: 'draft' | 'reviewed'; note?: string };
/** Reviewed 28 Sep as `notifications.csv` rows with the same English: the wording is taken as approved. */
const reviewed = (row: string, note?: string): Status => ({
  status: 'reviewed',
  note: `reviewed 28 Sep as notifications.csv ${row}${note ? `; ${note}` : ''}`,
});
const draft = (note?: string): Status => ({ status: 'draft', ...(note ? { note } : {}) });

/**
 * The first drafts (28 Sep): `reviewed` only where the exact English was already approved in Spanish (the note
 * names the row), every other line a `draft` for Evan's reviewer. `esSources` below lays the reviewer's pass over them.
 */
const drafted: Record<string, Status> = {
  'templates.greeting': reviewed('greeting'),
  'templates.toApprove': reviewed('toApprove'),
  'templates.toApproveTitle': reviewed('toApproveTitle'),
  'templates.ageBand.prereader': reviewed('ageBandLabel.prereader'),
  'templates.ageBand.reader': reviewed('ageBandLabel.reader'),
  'templates.photo': reviewed('approvalMetaHome'),
  'templates.tapToSeePhoto': reviewed('approvalMetaQueue'),
  'templates.approve': reviewed('approveWithBB'),
  'templates.possessive': reviewed(
    'possessive',
    'a Spanish line puts "de {name}" where the sentence needs it',
  ),
  'templates.photoFrom': reviewed('photoFrom'),
  'templates.readAloudTo': reviewed('readAloudTo'),
  'templates.quotedNote': draft('curly quotes kept; not in the reviewed sheet'),
  'templates.redeemTitle': reviewed('redeemTitle'),
  'templates.rewardRequest': reviewed('rewardRequestMeta'),
  'templates.balance': reviewed('redeemMeta'),
  'templates.scoreOk': reviewed('redeemMeta'),
  'templates.saveChild': reviewed('saveChild'),
  'templates.howDoesUse': reviewed('howDoesUse'),
  'templates.pickChoresFor': reviewed('pickChoresFor'),
  'templates.addChores': reviewed('addChores.one / addChores.other'),
  'templates.noChoresYet': reviewed('noChoresYet'),
  'templates.noChoresYetBody': reviewed('noChoresYetBody'),
  'templates.wantsTitle': reviewed('wantsTitle'),
  'templates.chargedOnRedeem': reviewed('chargedOnRedeem'),
  'templates.ofBalance': reviewed('ofBalance'),
  'templates.wantsBody': reviewed('wantsBody.one / wantsBody.other'),
  'templates.cancelledTitle': reviewed('cancelledTitle'),
  'templates.cancelledBody': reviewed('cancelledBody'),
  'templates.paidOutBy': reviewed('paidOutBy'),
  'templates.paidOut': reviewed('paidOutBy.noParent'),
  'templates.dollarLine': reviewed('dollarLine.one / dollarLine.other'),
  'templates.badgeCountPill': reviewed('badgeCountPill.one / badgeCountPill.other'),
  'templates.adventureEyebrow': reviewed('adventureEyebrow'),
  'templates.missionsDoneOf': reviewed('missionsDoneOf'),
  'templates.missionNodeLabel': reviewed('missionNodeLabel'),
  'templates.startMissionCta': reviewed('startMissionCta'),
  'templates.quickCheckEyebrow': reviewed('quickCheckEyebrow'),
  'templates.adventureCompleteEyebrow': reviewed('adventureCompleteEyebrow'),
  'templates.newBadgeLine': reviewed('newBadgeLine'),
  'templates.missionsRightLine': reviewed('missionsRightLine.one / missionsRightLine.other'),
  'templates.streakKeptLine': reviewed('streakKeptLine'),
  'templates.adventureUnlockedLine': reviewed('adventureUnlockedLine'),
  'templates.badgesEarnedPill': reviewed('badgesEarnedPill'),
  'templates.badgesToGoPill': reviewed('badgesToGoPill'),
  'templates.badgeSharedTitle': reviewed('badgeSharedTitle'),
  'templates.badgeSharedBody': reviewed('badgeSharedBody'),
  'templates.rewardNew.familyTitle': reviewed('rewardNewRow.family.title'),
  'templates.rewardNew.familyBody': reviewed('rewardNewRow.family.body.one / .other'),
  'templates.rewardNew.orgTitle': reviewed('rewardNewRow.org.title'),
  'templates.rewardNew.orgBody': reviewed('rewardNewRow.org.body'),
  'templates.screenTime.doneTitle': reviewed('screenTimeDoneTitle'),
  'templates.screenTime.askTitle': reviewed('screenTimeAskTitle'),
  'templates.screenTime.backTitle': reviewed('SCREEN_TIME_BACK_TITLE'),
  'templates.screenTime.backApproved': reviewed('screenTimeBackBody.approved'),
  'templates.screenTime.backUnlocked': reviewed('screenTimeBackBody.unlocked'),
  'templates.screenTime.backPaused': reviewed('screenTimeBackBody.paused'),
  'templates.screenTime.approvedEarly': reviewed('screenTimeNewsWords.approvedEarly.body'),
  'templates.screenTime.turnedOff': reviewed('screenTimeNewsWords.turnedOff.body'),
  'templates.screenTime.changed': reviewed('screenTimeNewsWords.changed.body'),
  'templates.clubJoinedTitle': reviewed('clubJoinedTitle'),
  'templates.clubApplicationThanks': reviewed('clubApplicationThanks'),

  'time.weekdaysLong': draft('lower-case, as Spanish writes them'),
  'time.weekdaysShort': draft('lower-case; "mié" and "sáb" keep their accents'),
  'time.monthsShort': draft(
    'abbreviations with a period, as the reviewed portal note writes "26 sep."',
  ),
  'time.am': draft('"a. m." is the Latin American form'),
  'time.pm': draft('"p. m."'),
  'time.clock': draft(),
  'time.shortDate': draft(
    '"15 sep." — day-month, short; the glossary\'s long form "15 de septiembre" is for full dates',
  ),
  'time.dateHeader': draft('"martes, 15 sep."'),
  'time.weekRangeSameMonth': draft('"7–13 sep."'),
  'time.weekRange': draft('"28 sep. – 4 oct."'),
  'time.dayMonthYear': draft('"12 mar. 2026"; reviewer: or "12 de mar. de 2026"?'),
  'time.monthYear': draft('"mar. 2025"'),
  'time.todayCapital': draft('as the reviewed app\'s "Hoy"'),
  'time.yesterdayCapital': draft(),
  'time.today': draft(),
  'time.yesterday': draft(),
  'time.daysAgo': draft('"hace 3 días" — the count moves after "hace"'),
  'time.upperLocale': draft('a locale, not words: day-group headings are upper-cased in it'),

  'schedule.daily': draft('feminine, agreeing with "tarea", as the reviewed app\'s "Diaria"'),
  'schedule.everyDay': draft('as the reviewed app\'s "todos los días"'),
  'schedule.schoolDays': draft(),
  'schedule.monFri': draft(),
  'schedule.weekdaysPlural': draft('"los sábados" — the article makes it a repeat'),

  'sizes.S': draft(
    'feminine, agreeing with "tarea", as the reviewed app\'s P01 "Pequeña, mediana, grande"',
  ),
  'sizes.M': draft(),
  'sizes.L': draft(),

  'bands.Building': draft("the glossary's reviewed band name, shown for the wire value"),
  'bands.Growing': draft('glossary'),
  'bands.Good': draft('glossary'),
  'bands.Great': draft('glossary'),
  'bands.Super': draft('glossary'),

  'oops.lowerLocale': draft('a locale, not words'),
  'oops.reasonLabel': draft('"Oops" stays English (glossary)'),
  'oops.noticeTitle': draft('"un Oops" (glossary: masculine)'),
  'oops.scorePart': draft('bare "score" → "puntaje", as the reviewed sheet does'),

  'gate.needsScore': draft(),
  'gate.noScoreYet': draft('"gate" → "requisito"; the glossary has no word for it'),

  'org.sees.firstName': draft(),
  'org.sees.avatar': draft(),
  'org.sees.ageBand': draft('"rango de edad", as the reviewed portal roster column'),
  'org.sees.groups': draft(),
  'org.sees.assignments': draft('"asignaciones", as the reviewed portal nav'),
  'org.sees.tasks': draft(),
  'org.sees.enrolledAt': draft(),
  'org.sees.scoreBand': draft(
    '"nivel" for band, the app drafter\'s word; "Bien" is the glossary band',
  ),
  'org.neverSees.lastName': draft(
    'each line stands alone under the list\'s own heading, so "o", never "ni"',
  ),
  'org.neverSees.dob': draft(),
  'org.neverSees.photos': draft(),
  'org.neverSees.scoreNumber': draft(),
  'org.neverSees.bb': draft(),
  'org.neverSees.siblings': draft(),
  'org.neverSees.familyChores': draft(),
  'org.neverSees.wishes': draft(),
  'org.neverSees.oops': draft(),
  'org.neverSees.parentContact': draft(),
  'org.neverSeesShort.identity': draft(),
  'org.neverSeesShort.photos': draft(),
  'org.neverSeesShort.home': draft(),
  'org.neverSeesShort.score': draft('"el Kredit Score" (glossary: masculine)'),
  'org.initialsTooLong': draft('staff-facing; `shortName` is a field name and stays'),

  'pandas.panda-white': draft('spoken to a pre-reader who taps it'),
  'pandas.panda-orange': draft(),
  'pandas.panda-blue': draft('"lentes", the Latin American word for glasses'),
  'pandas.panda-red': draft(),
  'pandas.panda-purple': draft('"audífonos", the Latin American word for headphones'),
  'pandas.panda-green': draft(),
  'pandas.panda-yellow': draft(),
  'pandas.panda-brown': draft('"café" for brown and "moño" for bow tie, the Latin American words'),

  'village.feeLine': draft('reviewer: "comisión" for fee'),
  'village.partnerTag': draft(
    '"socio", as the reviewed portal; the reviewer asked socio or aliado',
  ),
  'village.helpfulThanks': draft(),
  'village.problems.pickShelf': draft('"estante", as the reviewed portal'),
  'village.problems.title': draft(),
  'village.problems.titleMax': draft(),
  'village.problems.lineMax': draft(),
  'village.problems.ages': draft(),
  'village.problems.pictureKinds': draft(),
  'village.problems.picture': draft(),
  'village.problems.printableMinutes': draft(),
  'village.problems.readingTime': draft(),
  'village.problems.guideText': draft('"la guía" (glossary: feminine)'),
  'village.problems.shorterText': draft(),
  'village.problems.whichPrintable': draft(),
  'village.problems.eventStart': draft(),
  'village.problems.register': draft(),
  'village.problems.buttonGoes': draft(),
  'village.problems.whichPartner': draft(),
  'village.problems.partnerShelf': draft('the shelf names as the reviewed portal writes them'),
  'village.problems.https': draft(),
  'village.problems.buttonWords': draft('"texto del botón", as the reviewed portal'),
  'village.problems.pictureDraft': draft(),
  'village.problems.pictureRetired': draft(),
  'village.logo.type': draft(),
  'village.logo.file': draft(),
  'village.logo.size': draft(),
  'village.logo.png': draft(),
  'village.logo.svg': draft(),

  'help.question': draft(),
  'help.questionMax': draft(),
  'help.answer': draft(),
  'help.paragraphsMax': draft(),
  'help.paragraphMax': draft(),
  'help.order': draft(),

  'contentTeam.soloPublishNote': draft(
    '"Publisher" is the role\'s name in the admin; reviewer: keep or translate?',
  ),

  'adventure.needsTitle': draft(),
  'adventure.learnOff': draft(
    '"Lo que aprenderás" for "What you\'ll learn"; the section\'s own title needs the same',
  ),
  'adventure.learnNeedsWords': draft('"null" is a value in the request and stays'),
  'adventure.learnEdited': draft(),
  'adventure.noLearnToApprove': draft(),
  'adventure.learnApproved': draft(),
  'adventure.learnWithdrew': draft('"Retirar la aprobación", as the reviewed portal\'s Withdraw'),
  'adventure.edited': draft(),
  'adventure.reorder': draft(),
  'adventure.nothingToPublish': draft(),
  'adventure.approveLearnFirst': draft(),

  'questWhere.beatReader': draft('"escena" for beat, as the reviewed portal'),
  'questWhere.beatPrereader': draft(),
  'questWhere.beatChoice': draft('"opción", as the reviewed portal'),
  'questWhere.beatChoiceConsequence': draft('word order changes: "consecuencia de la opción A"'),
  'questWhere.question': draft(),
  'questWhere.questionPrereader': draft(),
  'questWhere.questionTile': draft('"casilla" for tile, as the reviewed portal'),
  'questWhere.questionTilePicture': draft(),
  'questWhere.questionTiles': draft(),
  'questWhere.questionFeedbackRight': draft('"comentario" for feedback, as the reviewed portal'),
  'questWhere.questionFeedback': draft(),
  'questWhere.questionAnswer': draft(),
  'questWhere.missionPicture': draft(),
  'questWhere.badge': draft(),
  'questWhere.mission': draft(),
  'questWhere.readerScript': draft('as the reviewed portal\'s "Guion para lectores"'),
  'questWhere.prereaderScript': draft('as the reviewed portal\'s "Guion para prelectores"'),

  'questDiff.title': draft(),
  'questDiff.levelName': draft('as the reviewed portal\'s "Nombre del nivel"'),
  'questDiff.mechanic': draft('as the reviewed portal\'s "Mecánica"'),
  'questDiff.pointsAt': draft(),
  'questDiff.badge': draft(),
  'questDiff.picture': draft(),
  'questDiff.none': draft('masculine, for a link'),
  'questDiff.typeChosen': draft(
    '"type" (a tile drawn as words) → "texto", as the reviewed portal\'s "todo texto"',
  ),
  'questDiff.fromTileId': draft('as the reviewed portal\'s "del id de la casilla"'),

  'questCheck.rules.spoken-missing': draft(),
  'questCheck.rules.beat-count': draft(),
  'questCheck.rules.too-long': draft(),
  'questCheck.rules.answer-not-tile': draft(),
  'questCheck.rules.tile-count': draft(),
  'questCheck.rules.mixed-row': draft(),
  'questCheck.rules.unknown-icon': draft(),
  'questCheck.rules.retired-icon': draft(),
  'questCheck.rules.draft-icon': draft(),
  'questCheck.rules.no-consequence': draft(),
  'questCheck.rules.consequence-digits': draft(),
  'questCheck.rules.bb': draft(),
  'questCheck.rules.parent-word': draft(
    'the check reads English lessons; the name of the rule is what translates',
  ),
  'questCheck.rules.pronoun': draft('as parent-word'),
  'questCheck.rules.unreviewed': draft(),
  'questCheck.rules.duplicate': draft(),
  'questCheck.rules.same-icon': draft(),
  'questCheck.rules.reading-level': draft(),
  'questCheck.rules.long-mission': draft(
    '"hermanas" agrees with "misión"; reviewer: or "que las demás"?',
  ),
  'questCheck.rules.line-break': draft(),
  'questCheck.notInLibrary': draft(),
  'questCheck.notPublished': draft(),
  'questCheck.beatCount': draft(),
  'questCheck.nothingToHear': draft(),
  'questCheck.noConsequence': draft(),
  'questCheck.tileCount': draft(),
  'questCheck.answerNotTile': draft(),
  'questCheck.retired': draft(),
  'questCheck.retiredName': draft(),
  'questCheck.useKey': draft('button: infinitive'),
  'questCheck.mixedRow': draft(),
  'questCheck.drawAllAsType': draft('button: infinitive'),
  'questCheck.sameIcon': draft(),
  'questCheck.bb': draft(),
  'questCheck.parentWord': draft('"un adulto", the glossary\'s grown-up'),
  'questCheck.pronoun': draft(),
  'questCheck.digits': draft(),
  'questCheck.digitsFix': draft('button: infinitive'),
  'questCheck.lineBreak': draft(),
  'questCheck.lineBreakFix': draft('button: infinitive'),
  'questCheck.readingLevel': draft(),
  'questCheck.tooLong': draft(),
  'questCheck.needsReview': draft(),
  'questCheck.duplicate': draft('{where} arrives lower-cased'),
  'questCheck.longMission': draft(),
};

/**
 * **Evan's reviewer's US pass (30 Sep): every line reviewed** — the status and note of each row the pass changed,
 * exactly as docs/handoff/spanish/domain.csv carries them (the sign-off; locale es-US throughout).
 */
const usPass30Sep: Record<string, Status> = {
  'templates.quotedNote': {
    status: 'reviewed',
    note: 'curly quotes kept; not in the reviewed sheet · US pass 30 Sep: reviewed',
  },
  'templates.clubJoinedTitle': {
    status: 'reviewed',
    note: 'reviewed 28 Sep as notifications.csv clubJoinedTitle · US pass 30 Sep: "comunidad" for the family\'s circle; the saying as it is known, "se necesita todo un pueblo"',
  },
  'time.weekdaysLong': {
    status: 'reviewed',
    note: 'lower-case, as Spanish writes them · US pass 30 Sep: reviewed',
  },
  'time.weekdaysShort': {
    status: 'reviewed',
    note: 'lower-case; "mié" and "sáb" keep their accents · US pass 30 Sep: reviewed',
  },
  'time.monthsShort': {
    status: 'reviewed',
    note: 'abbreviations with a period, as the reviewed portal note writes "26 sep." · US pass 30 Sep: reviewed',
  },
  'time.am': {
    status: 'reviewed',
    note: '"a. m." is the Latin American form · US pass 30 Sep: reviewed',
  },
  'time.pm': { status: 'reviewed', note: '"p. m." · US pass 30 Sep: reviewed' },
  'time.clock': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'time.shortDate': {
    status: 'reviewed',
    note: '"15 sep." — day-month, short; the glossary\'s long form "15 de septiembre" is for full dates · US pass 30 Sep: reviewed',
  },
  'time.dateHeader': { status: 'reviewed', note: '"martes, 15 sep." · US pass 30 Sep: reviewed' },
  'time.weekRangeSameMonth': { status: 'reviewed', note: '"7–13 sep." · US pass 30 Sep: reviewed' },
  'time.weekRange': { status: 'reviewed', note: '"28 sep. – 4 oct." · US pass 30 Sep: reviewed' },
  'time.dayMonthYear': {
    status: 'reviewed',
    note: '"12 mar. 2026"; reviewer: or "12 de mar. de 2026"? · US pass 30 Sep: reviewed',
  },
  'time.monthYear': { status: 'reviewed', note: '"mar. 2025" · US pass 30 Sep: reviewed' },
  'time.todayCapital': {
    status: 'reviewed',
    note: 'as the reviewed app\'s "Hoy" · US pass 30 Sep: reviewed',
  },
  'time.yesterdayCapital': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'time.today': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'time.yesterday': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'time.daysAgo': {
    status: 'reviewed',
    note: '"hace 3 días" — the count moves after "hace" · US pass 30 Sep: reviewed',
  },
  'time.upperLocale': {
    status: 'reviewed',
    note: 'a locale, not words: day-group headings are upper-cased in it · US pass 30 Sep: es-US, the locale for Spanish in the US (dates, times, numbers) · US pass 30 Sep: reviewed',
  },
  'schedule.daily': {
    status: 'reviewed',
    note: 'feminine, agreeing with "tarea", as the reviewed app\'s "Diaria" · US pass 30 Sep: reviewed',
  },
  'schedule.everyDay': {
    status: 'reviewed',
    note: 'as the reviewed app\'s "todos los días" · US pass 30 Sep: reviewed',
  },
  'schedule.schoolDays': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'schedule.monFri': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'schedule.weekdaysPlural': {
    status: 'reviewed',
    note: '"los sábados" — the article makes it a repeat · US pass 30 Sep: reviewed',
  },
  'sizes.S': {
    status: 'reviewed',
    note: 'feminine, agreeing with "tarea", as the reviewed app\'s P01 "Pequeña, mediana, grande" · US pass 30 Sep: reviewed',
  },
  'sizes.M': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'sizes.L': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'bands.Building': {
    status: 'reviewed',
    note: "the glossary's reviewed band name, shown for the wire value · US pass 30 Sep: reviewed",
  },
  'bands.Growing': { status: 'reviewed', note: 'glossary · US pass 30 Sep: reviewed' },
  'bands.Good': { status: 'reviewed', note: 'glossary · US pass 30 Sep: reviewed' },
  'bands.Great': { status: 'reviewed', note: 'glossary · US pass 30 Sep: reviewed' },
  'bands.Super': { status: 'reviewed', note: 'glossary · US pass 30 Sep: reviewed' },
  'oops.lowerLocale': {
    status: 'reviewed',
    note: 'a locale, not words · US pass 30 Sep: es-US, the locale for Spanish in the US (dates, times, numbers) · US pass 30 Sep: reviewed',
  },
  'oops.reasonLabel': {
    status: 'reviewed',
    note: '"Oops" stays English (glossary) · US pass 30 Sep: reviewed',
  },
  'oops.noticeTitle': {
    status: 'reviewed',
    note: '"un Oops" (glossary: masculine) · US pass 30 Sep: reviewed',
  },
  'oops.scorePart': {
    status: 'reviewed',
    note: 'bare "score" → "puntaje", as the reviewed sheet does · US pass 30 Sep: reviewed',
  },
  'gate.needsScore': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'gate.noScoreYet': {
    status: 'reviewed',
    note: '"gate" → "requisito"; the glossary has no word for it · US pass 30 Sep: reviewed',
  },
  'org.sees.firstName': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.sees.avatar': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.sees.ageBand': {
    status: 'reviewed',
    note: '"rango de edad", as the reviewed portal roster column · US pass 30 Sep: reviewed',
  },
  'org.sees.groups': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.sees.assignments': {
    status: 'reviewed',
    note: '"asignaciones", as the reviewed portal nav · US pass 30 Sep: reviewed',
  },
  'org.sees.tasks': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.sees.enrolledAt': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.sees.scoreBand': {
    status: 'reviewed',
    note: '"nivel" for band, the app drafter\'s word; "Bien" is the glossary band · US pass 30 Sep: reviewed',
  },
  'org.neverSees.lastName': {
    status: 'reviewed',
    note: 'each line stands alone under the list\'s own heading, so "o", never "ni" · US pass 30 Sep: reviewed',
  },
  'org.neverSees.dob': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.neverSees.photos': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.neverSees.scoreNumber': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.neverSees.bb': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.neverSees.siblings': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.neverSees.familyChores': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.neverSees.wishes': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.neverSees.oops': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.neverSees.parentContact': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.neverSeesShort.identity': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.neverSeesShort.photos': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.neverSeesShort.home': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'org.neverSeesShort.score': {
    status: 'reviewed',
    note: '"el Kredit Score" (glossary: masculine) · US pass 30 Sep: reviewed',
  },
  'org.initialsTooLong': {
    status: 'reviewed',
    note: 'staff-facing; `shortName` is a field name and stays · US pass 30 Sep: reviewed',
  },
  'pandas.panda-white': {
    status: 'reviewed',
    note: 'spoken to a pre-reader who taps it · US pass 30 Sep: reviewed',
  },
  'pandas.panda-orange': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'pandas.panda-blue': {
    status: 'reviewed',
    note: '"lentes", the Latin American word for glasses · US pass 30 Sep: reviewed',
  },
  'pandas.panda-red': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'pandas.panda-purple': {
    status: 'reviewed',
    note: '"audífonos", the Latin American word for headphones · US pass 30 Sep: reviewed',
  },
  'pandas.panda-green': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'pandas.panda-yellow': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'pandas.panda-brown': {
    status: 'reviewed',
    note: '"café" for brown and "moño" for bow tie, the Latin American words · US pass 30 Sep: reviewed',
  },
  'village.feeLine': {
    status: 'reviewed',
    note: 'reviewer: "comisión" for fee · US pass 30 Sep: reviewed',
  },
  'village.partnerTag': {
    status: 'reviewed',
    note: '"socio", as the reviewed portal; the reviewer asked socio or aliado · US pass 30 Sep: reviewed',
  },
  'village.helpfulThanks': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.pickShelf': {
    status: 'reviewed',
    note: '"estante", as the reviewed portal · US pass 30 Sep: reviewed',
  },
  'village.problems.title': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.titleMax': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.lineMax': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.ages': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.pictureKinds': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.picture': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.printableMinutes': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.readingTime': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.guideText': {
    status: 'reviewed',
    note: '"la guía" (glossary: feminine) · US pass 30 Sep: reviewed',
  },
  'village.problems.shorterText': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.whichPrintable': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.eventStart': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.register': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.buttonGoes': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.whichPartner': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.partnerShelf': {
    status: 'reviewed',
    note: 'the shelf names as the reviewed portal writes them · US pass 30 Sep: reviewed',
  },
  'village.problems.https': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.buttonWords': {
    status: 'reviewed',
    note: '"texto del botón", as the reviewed portal · US pass 30 Sep: reviewed',
  },
  'village.problems.pictureDraft': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.problems.pictureRetired': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.logo.type': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.logo.file': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.logo.size': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.logo.png': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'village.logo.svg': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'help.question': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'help.questionMax': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'help.answer': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'help.paragraphsMax': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'help.paragraphMax': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'help.order': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'contentTeam.soloPublishNote': {
    status: 'reviewed',
    note: '"Publisher" is the role\'s name in the admin; reviewer: keep or translate? · US pass 30 Sep: reviewed',
  },
  'adventure.needsTitle': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'adventure.learnOff': {
    status: 'reviewed',
    note: '"Lo que aprenderás" for "What you\'ll learn"; the section\'s own title needs the same · US pass 30 Sep: reviewed',
  },
  'adventure.learnNeedsWords': {
    status: 'reviewed',
    note: '"null" is a value in the request and stays · US pass 30 Sep: reviewed',
  },
  'adventure.learnEdited': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'adventure.noLearnToApprove': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'adventure.learnApproved': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'adventure.learnWithdrew': {
    status: 'reviewed',
    note: '"Retirar la aprobación", as the reviewed portal\'s Withdraw · US pass 30 Sep: reviewed',
  },
  'adventure.edited': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'adventure.reorder': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'adventure.nothingToPublish': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'adventure.approveLearnFirst': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questWhere.beatReader': {
    status: 'reviewed',
    note: '"escena" for beat, as the reviewed portal · US pass 30 Sep: reviewed',
  },
  'questWhere.beatPrereader': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questWhere.beatChoice': {
    status: 'reviewed',
    note: '"opción", as the reviewed portal · US pass 30 Sep: reviewed',
  },
  'questWhere.beatChoiceConsequence': {
    status: 'reviewed',
    note: 'word order changes: "consecuencia de la opción A" · US pass 30 Sep: reviewed',
  },
  'questWhere.question': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questWhere.questionPrereader': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questWhere.questionTile': {
    status: 'reviewed',
    note: '"casilla" for tile, as the reviewed portal · US pass 30 Sep: reviewed',
  },
  'questWhere.questionTilePicture': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questWhere.questionTiles': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questWhere.questionFeedbackRight': {
    status: 'reviewed',
    note: '"comentario" for feedback, as the reviewed portal · US pass 30 Sep: reviewed',
  },
  'questWhere.questionFeedback': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questWhere.questionAnswer': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questWhere.missionPicture': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questWhere.badge': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questWhere.mission': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questWhere.readerScript': {
    status: 'reviewed',
    note: 'as the reviewed portal\'s "Guion para lectores" · US pass 30 Sep: reviewed',
  },
  'questWhere.prereaderScript': {
    status: 'reviewed',
    note: 'as the reviewed portal\'s "Guion para prelectores" · US pass 30 Sep: reviewed',
  },
  'questDiff.title': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questDiff.levelName': {
    status: 'reviewed',
    note: 'as the reviewed portal\'s "Nombre del nivel" · US pass 30 Sep: reviewed',
  },
  'questDiff.mechanic': {
    status: 'reviewed',
    note: 'as the reviewed portal\'s "Mecánica" · US pass 30 Sep: reviewed',
  },
  'questDiff.pointsAt': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questDiff.badge': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questDiff.picture': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questDiff.none': {
    status: 'reviewed',
    note: 'masculine, for a link · US pass 30 Sep: reviewed',
  },
  'questDiff.typeChosen': {
    status: 'reviewed',
    note: '"type" (a tile drawn as words) → "texto", as the reviewed portal\'s "todo texto" · US pass 30 Sep: reviewed',
  },
  'questDiff.fromTileId': {
    status: 'reviewed',
    note: 'as the reviewed portal\'s "del id de la casilla" · US pass 30 Sep: reviewed',
  },
  'questCheck.rules.spoken-missing': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.beat-count': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.too-long': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.answer-not-tile': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.tile-count': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.mixed-row': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.unknown-icon': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.retired-icon': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.draft-icon': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.no-consequence': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.consequence-digits': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.bb': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.parent-word': {
    status: 'reviewed',
    note: 'the check reads English lessons; the name of the rule is what translates · US pass 30 Sep: reviewed',
  },
  'questCheck.rules.pronoun': {
    status: 'reviewed',
    note: 'as parent-word · US pass 30 Sep: reviewed',
  },
  'questCheck.rules.unreviewed': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.duplicate': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.same-icon': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.reading-level': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.rules.long-mission': {
    status: 'reviewed',
    note: '"hermanas" agrees with "misión"; reviewer: or "que las demás"? · US pass 30 Sep: reads more naturally · US pass 30 Sep: reviewed',
  },
  'questCheck.rules.line-break': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.notInLibrary': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.notPublished': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.beatCount': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.nothingToHear': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.noConsequence': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.tileCount': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.answerNotTile': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.retired': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.retiredName': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.useKey': {
    status: 'reviewed',
    note: 'button: infinitive · US pass 30 Sep: reviewed',
  },
  'questCheck.mixedRow': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.drawAllAsType': {
    status: 'reviewed',
    note: 'button: infinitive · US pass 30 Sep: reviewed',
  },
  'questCheck.sameIcon': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.bb': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.parentWord': {
    status: 'reviewed',
    note: '"un adulto", the glossary\'s grown-up · US pass 30 Sep: reviewed',
  },
  'questCheck.pronoun': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.digits': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.digitsFix': {
    status: 'reviewed',
    note: 'button: infinitive · US pass 30 Sep: reviewed',
  },
  'questCheck.lineBreak': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.lineBreakFix': {
    status: 'reviewed',
    note: 'button: infinitive · US pass 30 Sep: reviewed',
  },
  'questCheck.readingLevel': {
    status: 'reviewed',
    note: 'US pass 30 Sep: reads more naturally · US pass 30 Sep: reviewed',
  },
  'questCheck.tooLong': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.needsReview': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
  'questCheck.duplicate': {
    status: 'reviewed',
    note: '{where} arrives lower-cased · US pass 30 Sep: reviewed',
  },
  'questCheck.longMission': { status: 'reviewed', note: 'US pass 30 Sep: reviewed' },
};

/**
 * **Where each Spanish line stands**, by its key: the first drafts and the notifications rows as they were, then the
 * reviewer's US pass over them. The test in `copy/copy.test.ts` holds this to the table: every Spanish line has an
 * entry, and nothing else does.
 */
export const esSources: Record<string, Status> = { ...drafted, ...usPass30Sep };
